import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarIcon, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { DateRange } from "@/api/business-analytics";

interface DateRangeFilterProps {
  dateRange: DateRange;
  onDateRangeChange: (dateRange: DateRange) => void;
}

type PresetOption = {
  label: string;
  value: string;
  getRange: () => DateRange;
};

const presetOptions: PresetOption[] = [
  {
    label: "Today",
    value: "today",
    getRange: () => ({
      from: new Date(),
      to: new Date(),
    }),
  },
  {
    label: "Yesterday",
    value: "yesterday",
    getRange: () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return { from: yesterday, to: yesterday };
    },
  },
  {
    label: "Current Month",
    value: "currentMonth",
    getRange: () => {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { from: startOfMonth, to: endOfMonth };
    },
  },
  {
    label: "Last 7 days",
    value: "7days",
    getRange: () => {
      const minDate = new Date(2025, 6, 1); // July 1, 2025
      const fromDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      return {
        from: fromDate < minDate ? minDate : fromDate,
        to: new Date(),
      };
    },
  },
  {
    label: "Last 28 days",
    value: "28days",
    getRange: () => {
      const minDate = new Date(2025, 6, 1); // July 1, 2025
      const fromDate = new Date(Date.now() - 28 * 24 * 60 * 60 * 1000);
      return {
        from: fromDate < minDate ? minDate : fromDate,
        to: new Date(),
      };
    },
  },
  {
    label: "Last 90 days",
    value: "90days",
    getRange: () => {
      const minDate = new Date(2025, 6, 1); // July 1, 2025
      const fromDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
      return {
        from: fromDate < minDate ? minDate : fromDate,
        to: new Date(),
      };
    },
  },
  {
    label: "Last 365 days",
    value: "365days",
    getRange: () => {
      const minDate = new Date(2025, 6, 1); // July 1, 2025
      const fromDate = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000);
      return {
        from: fromDate < minDate ? minDate : fromDate,
        to: new Date(),
      };
    },
  },
  {
    label: "Lifetime",
    value: "lifetime",
    getRange: () => ({
      from: new Date(2025, 6, 1), // July 1, 2025
      to: new Date(),
    }),
  },
];

const yearOptions = [
  { label: "2025", value: "2025" },
];

const monthOptions = [
  { label: "January", value: "0" },
  { label: "February", value: "1" },
  { label: "March", value: "2" },
  { label: "April", value: "3" },
  { label: "May", value: "4" },
  { label: "June", value: "5" },
  { label: "July", value: "6" },
  { label: "August", value: "7" },
  { label: "September", value: "8" },
  { label: "October", value: "9" },
  { label: "November", value: "10" },
  { label: "December", value: "11" },
];

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  dateRange,
  onDateRangeChange,
}) => {
  // Initialize selected preset based on the passed date range
  const getInitialPreset = () => {
    const now = new Date();
    const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfCurrentMonth = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0
    );
    const twentyEightDaysAgo = new Date(Date.now() - 28 * 24 * 60 * 60 * 1000);

    // Check if the date range matches current month
    if (
      dateRange.from.getTime() === startOfCurrentMonth.getTime() &&
      dateRange.to.getTime() === endOfCurrentMonth.getTime()
    ) {
      return "currentMonth";
    }

    // Check if the date range matches "Last 28 days"
    if (
      dateRange.from.getTime() === twentyEightDaysAgo.getTime() &&
      dateRange.to.getTime() === now.getTime()
    ) {
      return "28days";
    }

    // Check other presets
    for (const option of presetOptions) {
      const range = option.getRange();
      if (
        dateRange.from.getTime() === range.from.getTime() &&
        dateRange.to.getTime() === range.to.getTime()
      ) {
        return option.value;
      }
    }

    return ""; // No preset matches, use custom range
  };

  const [selectedPreset, setSelectedPreset] = useState(getInitialPreset);
  const [calendarRange, setCalendarRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: dateRange.from,
    to: dateRange.to,
  });

  // Update selected preset when date range changes from outside
  useEffect(() => {
    const newPreset = getInitialPreset();
    setSelectedPreset(newPreset);
    setCalendarRange({
      from: dateRange.from,
      to: dateRange.to,
    });
  }, [dateRange.from, dateRange.to]);

  const handlePresetChange = (preset: string) => {
    setSelectedPreset(preset);
    const option = presetOptions.find((opt) => opt.value === preset);
    if (option) {
      const range = option.getRange();
      onDateRangeChange(range);
      setCalendarRange({ from: range.from, to: range.to });
    }
  };

  const handleYearChange = (year: string) => {
    const yearNum = parseInt(year);
    // For 2025, start from July 1st; for other years, start from January 1st
    const startMonth = yearNum === 2025 ? 6 : 0; // July = month 6 (0-indexed)
    const range = {
      from: new Date(yearNum, startMonth, 1), // July 1st for 2025, January 1st for others
      to: new Date(yearNum, 11, 31), // December 31st of the year
    };
    onDateRangeChange(range);
    setCalendarRange({ from: range.from, to: range.to });
    setSelectedPreset("");
  };

  const handleMonthChange = (month: string) => {
    const currentYear = new Date().getFullYear();
    const monthNum = parseInt(month);
    const range = {
      from: new Date(currentYear, monthNum, 1),
      to: new Date(currentYear, monthNum + 1, 0), // Last day of the month
    };
    onDateRangeChange(range);
    setCalendarRange({ from: range.from, to: range.to });
    setSelectedPreset("");
  };

  const handleCalendarSelect = (
    range: { from: Date | undefined; to: Date | undefined } | undefined
  ) => {
    if (range?.from && range?.to) {
      // Ensure dates are not before July 1, 2025
      const minDate = new Date(2025, 6, 1);
      const fromDate = range.from < minDate ? minDate : range.from;
      const toDate = range.to < minDate ? minDate : range.to;
      
      setCalendarRange({ from: fromDate, to: toDate });
      onDateRangeChange({ from: fromDate, to: toDate });
      setSelectedPreset("");
    }
  };

  return (
    <div className="flex flex-wrap gap-2 items-center">
      {/* Preset Options */}
      <Select value={selectedPreset} onValueChange={handlePresetChange}>
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="Select period" />
        </SelectTrigger>
        <SelectContent>
          {presetOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Year Filter */}
      <Select onValueChange={handleYearChange}>
        <SelectTrigger className="w-[100px]">
          <SelectValue placeholder="Year" />
        </SelectTrigger>
        <SelectContent>
          {yearOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Month Filter */}
      <Select onValueChange={handleMonthChange}>
        <SelectTrigger className="w-[120px]">
          <SelectValue placeholder="Month" />
        </SelectTrigger>
        <SelectContent>
          {monthOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Custom Range Picker */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="min-w-[200px] justify-start text-left font-normal"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {calendarRange.from ? (
              calendarRange.to ? (
                <>
                  {format(calendarRange.from, "MMM dd, yyyy")} -{" "}
                  {format(calendarRange.to, "MMM dd, yyyy")}
                </>
              ) : (
                format(calendarRange.from, "MMM dd, yyyy")
              )
            ) : (
              <span>Pick a date range</span>
            )}
            <ChevronDown className="ml-auto h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={calendarRange.from}
            selected={calendarRange}
            onSelect={handleCalendarSelect}
            numberOfMonths={2}
            fromDate={new Date(2025, 6, 1)} // July 1, 2025 - minimum selectable date
            disabled={(date) => date < new Date(2025, 6, 1)} // Disable dates before July 1, 2025
          />
        </PopoverContent>
      </Popover>

      {/* Current Range Display */}
      <div className="text-sm text-gray-500">
        {format(dateRange.from, "MMM dd, yyyy")} -{" "}
        {format(dateRange.to, "MMM dd, yyyy")}
      </div>
    </div>
  );
};
